import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import { Box, Plus, Edit, Trash2, Search, MapPin, Gauge, RefreshCw, Building2, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const PAGE_SIZE = 50;

const Spaces = () => {
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  
  // Lazy loading
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({ total: 0, occupied: 0, vacant: 0, total_meters: 0 });
  const sentinelRef = useRef(null);
  const searchTimerRef = useRef(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // CSV Upload states
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    location_id: '',
    unit_number: '',
    floor_number: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    space_type: 'unit',
    area_sqft: '',
    occupancy_status: 'vacant',
    tenant_name: '',
    pms_unit_code: '',
    bp_space_id: '',
    status: 'active',
  });

  useEffect(() => {
    const initializePage = async () => {
      try {
        const { data: accounts } = await api.get('/auth/accounts');
        const storedAccountStr = localStorage.getItem('current_account');
        const storedAccount = storedAccountStr ? JSON.parse(storedAccountStr) : null;
        
        let account = null;
        if (storedAccount) {
          account = accounts.find(a => a.id === storedAccount.id);
        }
        if (!account && accounts.length > 0) {
          account = accounts[0];
        }
        
        if (!account) {
          navigate('/dashboard');
          return;
        }
        
        localStorage.setItem('current_account', JSON.stringify(account));
        
        if (!account.enabled_apps?.ubm) {
          navigate('/dashboard');
          return;
        }
        
        setCurrentAccount(account);
        setInitializing(false);
      } catch (error) {
        console.error('Failed to initialize:', error);
        navigate('/dashboard');
      }
    };
    
    initializePage();
  }, [navigate]);

  const fetchStats = useCallback(async (accountId) => {
    try {
      const { data } = await api.get(`/ubm/accounts/${accountId}/spaces/stats`);
      setStats(data);
    } catch { /* stats are non-critical */ }
  }, []);

  const fetchPage = useCallback(async (accountId, pageNum, append = false) => {
    if (!accountId) return;
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    
    try {
      const params = new URLSearchParams({ page: pageNum, page_size: PAGE_SIZE });
      if (searchQuery) params.append('search', searchQuery);
      if (locationFilter !== 'all') params.append('location_id', locationFilter);
      if (typeFilter !== 'all') params.append('space_type', typeFilter);
      
      const [spacesRes, locationsRes] = await Promise.all([
        api.get(`/ubm/accounts/${accountId}/spaces?${params}`),
        ...(pageNum === 1 ? [api.get(`/ubm/accounts/${accountId}/locations`)] : []),
      ]);
      const spacesData = spacesRes.data;
      const newItems = spacesData.items || [];
      
      if (append) {
        setSpaces(prev => [...prev, ...newItems]);
      } else {
        setSpaces(newItems);
      }
      setHasMore(pageNum < (spacesData.total_pages || 0));
      if (locationsRes) setLocations(locationsRes.data || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to load data:', error);
      }
      if (!append) { setSpaces([]); setLocations([]); }
      setHasMore(false);
    }
    setLoading(false);
    setLoadingMore(false);
  }, [searchQuery, locationFilter, typeFilter]);

  // Initial load + stats
  useEffect(() => {
    if (currentAccount && !initializing) {
      setPage(1);
      setSpaces([]);
      setHasMore(true);
      fetchPage(currentAccount.id, 1);
      fetchStats(currentAccount.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount, initializing]);

  // Filter changes -> reset and reload
  useEffect(() => {
    if (currentAccount && !initializing) {
      setPage(1);
      setSpaces([]);
      setHasMore(true);
      fetchPage(currentAccount.id, 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFilter, typeFilter]);

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      if (currentAccount) {
        setPage(1);
        setSpaces([]);
        setHasMore(true);
        fetchPage(currentAccount.id, 1);
      }
    }, 400);
  };

  // Scroll-based lazy loading
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;
      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight - 300) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchPage(currentAccount?.id, nextPage, true);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, page, currentAccount]);

  const refreshAll = () => {
    if (!currentAccount) return;
    setPage(1);
    setSpaces([]);
    setHasMore(true);
    fetchPage(currentAccount.id, 1);
    fetchStats(currentAccount.id);
  };

  const resetForm = () => {
    setForm({
      location_id: '',
      unit_number: '',
      floor_number: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      space_type: 'unit',
      area_sqft: '',
      occupancy_status: 'vacant',
      tenant_name: '',
      pms_unit_code: '',
      bp_space_id: '',
      status: 'active',
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.location_id) {
      toast.error('Please select a location');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/ubm/accounts/${currentAccount.id}/spaces`, form);
      toast.success('Unit added successfully');
      setShowAddModal(false);
      resetForm();
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add unit');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedSpace) return;
    
    setSaving(true);
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/spaces/${selectedSpace.id}`, form);
      toast.success('Unit updated successfully');
      setShowEditModal(false);
      setSelectedSpace(null);
      resetForm();
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update unit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSpace) return;
    
    try {
      await api.delete(`/ubm/accounts/${currentAccount.id}/spaces/${selectedSpace.id}`);
      toast.success('Unit deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedSpace(null);
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete unit');
    }
  };

  const openEditModal = (space) => {
    setSelectedSpace(space);
    setForm({
      location_id: space.location_id || '',
      unit_number: space.unit_number || '',
      floor_number: space.floor_number || '',
      address: space.address || '',
      city: space.city || '',
      state: space.state || '',
      zip: space.zip || '',
      space_type: space.space_type || 'unit',
      area_sqft: space.area_sqft || '',
      occupancy_status: space.occupancy_status || 'vacant',
      tenant_name: space.tenant_name || '',
      pms_unit_code: space.pms_unit_code || '',
      bp_space_id: space.bp_space_id || '',
      status: space.status || 'active',
    });
    setShowEditModal(true);
  };

  const openDeleteConfirm = (space) => {
    setSelectedSpace(space);
    setShowDeleteConfirm(true);
  };

  // CSV Upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/ubm/accounts/${currentAccount.id}/spaces/csv-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'units_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await api.post(`/ubm/accounts/${currentAccount.id}/spaces/csv-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResults(data);
      
      if (data.created > 0 || data.updated > 0) {
        toast.success(`Created ${data.created}, Updated ${data.updated} units`);
        refreshAll();
      }
      
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
      setUploadResults({ errors: [{ error: error.response?.data?.detail || 'Upload failed' }] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // All filtering is server-side now
  const filteredSpaces = spaces;

  const spaceTypes = [
    { value: 'unit', label: 'Unit' },
    { value: 'office', label: 'Office' },
    { value: 'common_area', label: 'Common Area' },
    { value: 'basement', label: 'Basement' },
    { value: 'car_park', label: 'Car Park' },
    { value: 'retail', label: 'Retail' },
    { value: 'storage', label: 'Storage' },
  ];

  const occupancyStatuses = [
    { value: 'occupied', label: 'Occupied' },
    { value: 'vacant', label: 'Vacant' },
    { value: 'under_renovation', label: 'Under Renovation' },
  ];

  const getSpaceTypeLabel = (type) => {
    const found = spaceTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getOccupancyBadgeClass = (status) => {
    switch (status) {
      case 'occupied':
        return 'bg-green-100 text-green-800';
      case 'vacant':
        return 'bg-yellow-100 text-yellow-800';
      case 'under_renovation':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleExportCSV = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/spaces/export`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `units_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Box className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Units</h1>
              <p className="text-sm text-muted-foreground">
                Manage units, offices, and common areas within your properties
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="rounded-none"
              data-testid="export-csv-btn"
              disabled={stats.total === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(true)}
              className="rounded-none"
              data-testid="upload-csv-btn"
              disabled={locations.length === 0}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload CSV
            </Button>
            <Button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="rounded-none"
              data-testid="add-unit-btn"
              disabled={locations.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Unit
            </Button>
          </div>
        </div>

        {locations.length === 0 && !loading && (
          <div className="border border-yellow-200 bg-yellow-50 p-4 rounded-none">
            <p className="text-sm text-yellow-800">
              <strong>No properties found.</strong> You need to create a property first before adding units.
              <Button 
                variant="link" 
                className="p-0 h-auto ml-2 text-yellow-800 underline"
                onClick={() => navigate('/ubm/properties')}
              >
                Go to Properties
              </Button>
            </p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Box className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide font-medium">Total Units</span>
            </div>
            <p className="text-3xl font-black tracking-tighter">{stats.total}</p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <Building2 className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide font-medium">Occupied</span>
            </div>
            <p className="text-3xl font-black tracking-tighter">
              {stats.occupied}
            </p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center gap-2 text-yellow-600 mb-1">
              <Box className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide font-medium">Vacant</span>
            </div>
            <p className="text-3xl font-black tracking-tighter">
              {stats.vacant}
            </p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Gauge className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide font-medium">Total Meters</span>
            </div>
            <p className="text-3xl font-black tracking-tighter">
              {stats.total_meters}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search units..."
                className="rounded-none pl-9"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') refreshAll(); }}
              />
            </div>
          </div>
          <div className="w-40">
            <Select value={locationFilter} onValueChange={(v) => { setLocationFilter(v); }}>
              <SelectTrigger className="rounded-none">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="rounded-none">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {spaceTypes.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={refreshAll}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Table */}
        <div className="border border-border rounded-none bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Unit #</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Property</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Type</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Address</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Status</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Meters</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {initializing || loading ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-muted-foreground">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      {initializing ? 'Initializing...' : 'Loading units...'}
                    </td>
                  </tr>
                ) : filteredSpaces.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-muted-foreground">
                      <Box className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No units found</p>
                      <p className="text-xs mt-1">Add units to track utility meters</p>
                    </td>
                  </tr>
                ) : (
                  filteredSpaces.map((space) => (
                    <tr key={space.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3">
                        <p className="font-medium font-mono">{space.unit_number || 'N/A'}</p>
                      </td>
                      <td className="p-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{space.location_name}</span>
                          </div>
                          {space.bp_space_id && (
                            <p className="text-xs text-muted-foreground font-mono mt-0.5 ml-5">BP - {space.bp_space_id}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="rounded-none text-xs">
                          {getSpaceTypeLabel(space.space_type)}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <span className="text-sm">{[space.address, space.city, space.state].filter(Boolean).join(', ') || '-'}</span>
                      </td>
                      <td className="p-3">
                        <div className="space-y-1">
                          <Badge variant={space.status === 'active' ? 'default' : 'secondary'} className="rounded-none text-xs block w-fit">
                            {(space.status || 'active').charAt(0).toUpperCase() + (space.status || 'active').slice(1)}
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className={`rounded-none text-xs block w-fit ${
                              space.occupancy_status === 'occupied' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                              space.occupancy_status === 'vacant' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                              'border-gray-300 text-gray-600'
                            }`}
                          >
                            {(space.occupancy_status || 'vacant').charAt(0).toUpperCase() + (space.occupancy_status || 'vacant').slice(1)}
                          </Badge>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-medium">{space.meter_count || 0}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-none"
                            onClick={() => openEditModal(space)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-none text-destructive hover:text-destructive"
                            onClick={() => openDeleteConfirm(space)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Infinite scroll sentinel + count */}
        <div className="flex items-center justify-between px-1">
          <p className="text-xs text-muted-foreground">
            Showing {filteredSpaces.length} of {stats.total} unit(s)
          </p>
        </div>
        {loadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
            <span className="text-sm text-muted-foreground">Loading more...</span>
          </div>
        )}
        <div ref={sentinelRef} className="h-1" />
      </div>

      {/* Add Unit Side Panel */}
      <Sheet open={showAddModal} onOpenChange={setShowAddModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="add-unit-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Add Unit</SheetTitle>
            <SheetDescription>Add a new unit, office, or common area to a property.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAdd} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Property *</Label>
                <Select value={form.location_id} onValueChange={(value) => setForm({ ...form, location_id: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select a property" /></SelectTrigger>
                  <SelectContent>{locations.map(loc => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit Number</Label>
                <Input className="rounded-none mt-1" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} placeholder="e.g., 101, Suite A" />
              </div>
              <div>
                <Label>Unit Type</Label>
                <Select value={form.space_type} onValueChange={(value) => setForm({ ...form, space_type: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{spaceTypes.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Floor Number</Label>
                <Input className="rounded-none mt-1" type="number" value={form.floor_number} onChange={(e) => setForm({ ...form, floor_number: e.target.value })} placeholder="e.g., 1" />
              </div>
              <div>
                <Label>Area (sqft)</Label>
                <Input className="rounded-none mt-1" type="number" value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} placeholder="e.g., 850" />
              </div>
              <div>
                <Label>Occupancy Status</Label>
                <Select value={form.occupancy_status} onValueChange={(value) => setForm({ ...form, occupancy_status: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{occupancyStatuses.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-unit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>PMS Unit Code</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.pms_unit_code} onChange={(e) => setForm({ ...form, pms_unit_code: e.target.value })} placeholder="e.g., UNIT101" />
              </div>
              <div>
                <Label>BP Space ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.bp_space_id} onChange={(e) => setForm({ ...form, bp_space_id: e.target.value })} placeholder="BillPortal reference" />
              </div>
              <div className="col-span-2">
                <Label>Tenant Name</Label>
                <Input className="rounded-none mt-1" value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} placeholder="e.g., John Smith (if occupied)" />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input className="rounded-none mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="e.g., 123 Main St" />
              </div>
              <div>
                <Label>City</Label>
                <Input className="rounded-none mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label>State</Label>
                <Input className="rounded-none mt-1" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} maxLength={2} />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input className="rounded-none mt-1" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-add-unit-btn">{saving ? 'Adding...' : 'Add Unit'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Edit Unit Side Panel */}
      <Sheet open={showEditModal} onOpenChange={setShowEditModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="edit-unit-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Edit Unit</SheetTitle>
            <SheetDescription>Update unit details.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleEdit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Number</Label>
                <Input className="rounded-none mt-1" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} />
              </div>
              <div>
                <Label>Unit Type</Label>
                <Select value={form.space_type} onValueChange={(value) => setForm({ ...form, space_type: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{spaceTypes.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Floor Number</Label>
                <Input className="rounded-none mt-1" type="number" value={form.floor_number} onChange={(e) => setForm({ ...form, floor_number: e.target.value })} />
              </div>
              <div>
                <Label>Area (sqft)</Label>
                <Input className="rounded-none mt-1" type="number" value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} />
              </div>
              <div>
                <Label>Occupancy Status</Label>
                <Select value={form.occupancy_status} onValueChange={(value) => setForm({ ...form, occupancy_status: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{occupancyStatuses.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-unit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>PMS Unit Code</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.pms_unit_code} onChange={(e) => setForm({ ...form, pms_unit_code: e.target.value })} />
              </div>
              <div>
                <Label>BP Space ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.bp_space_id} onChange={(e) => setForm({ ...form, bp_space_id: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Tenant Name</Label>
                <Input className="rounded-none mt-1" value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input className="rounded-none mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <Label>City</Label>
                <Input className="rounded-none mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label>State</Label>
                <Input className="rounded-none mt-1" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} maxLength={2} />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input className="rounded-none mt-1" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-edit-unit-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Unit</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedSpace?.unit_number || 'this unit'}"? 
              This will also delete all associated meters. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" className="rounded-none" onClick={handleDelete}>
              Delete Unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadModal} onOpenChange={(open) => { setShowUploadModal(open); if (!open) setUploadResults(null); }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Upload Units CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk create or update units. Existing units will be matched by PMS Unit Code or Property + Unit Number.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Download Template */}
            <div className="border border-dashed border-border rounded-none p-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Download Template</p>
                  <p className="text-xs text-muted-foreground">Get a CSV template with all required columns</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-none" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Template
                </Button>
              </div>
            </div>

            {/* Upload Area */}
            <div className="border border-dashed border-border rounded-none p-6 text-center bg-muted/10 hover:bg-muted/20 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {uploading ? 'Uploading...' : 'Click to upload CSV file'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  CSV files only
                </p>
              </label>
            </div>

            {/* Upload Results */}
            {uploadResults && (
              <div className="space-y-2">
                {(uploadResults.created > 0 || uploadResults.updated > 0) && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-2 rounded-none">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Created: {uploadResults.created}, Updated: {uploadResults.updated}</span>
                  </div>
                )}
                {uploadResults.errors?.length > 0 && (
                  <div className="bg-red-50 p-2 rounded-none">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{uploadResults.errors.length} Error(s)</span>
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {uploadResults.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-700">
                          {err.row ? `Row ${err.row}: ` : ''}{err.error}
                        </p>
                      ))}
                      {uploadResults.errors.length > 5 && (
                        <p className="text-xs text-red-500 mt-1">
                          ...and {uploadResults.errors.length - 5} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setShowUploadModal(false); setUploadResults(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Spaces;
